import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Send, Bot, User, Plus, Paperclip, Trash2, Image as ImageIcon, X, BookPlus, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import NavigationMenu from '../components/NavigationMenu';
import VoiceControl from '../components/VoiceControl';
import { detectRecipeInMessage, parseRecipeFromMessage } from '../utils/recipeParser';
import { useChat } from '../contexts/ChatContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pool de sugestões (usando keys de tradução)
const SUGGESTION_KEYS = [
  'chatAssistant.suggestion1',
  'chatAssistant.suggestion2',
  'chatAssistant.suggestion3',
  'chatAssistant.suggestion4',
  'chatAssistant.suggestion5',
  'chatAssistant.suggestion6',
  'chatAssistant.suggestion7',
  'chatAssistant.suggestion8',
  'chatAssistant.suggestion9',
  'chatAssistant.suggestion10',
  'chatAssistant.suggestion11',
  'chatAssistant.suggestion12',
  'chatAssistant.suggestion13',
  'chatAssistant.suggestion14',
  'chatAssistant.suggestion15',
  'chatAssistant.suggestion16',
  'chatAssistant.suggestion17',
  'chatAssistant.suggestion18',
  'chatAssistant.suggestion19',
  'chatAssistant.suggestion20'
];

// Função para selecionar 4 sugestões baseadas no dia
const getDailySuggestions = (t) => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
  
  // Usar o dia do ano como seed para seleção consistente no mesmo dia
  const shuffled = [...SUGGESTION_KEYS];
  let seed = dayOfYear;
  
  // Shuffle usando o dia como seed
  for (let i = shuffled.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = seed % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Retornar as traduções das 4 primeiras keys
  return shuffled.slice(0, 4).map(key => t(key));
};

const ChatAssistantPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Use ChatContext instead of local state
  const {
    conversations,
    currentConversationId,
    messages,
    loading: contextLoading,
    messagesRef,
    currentConversationIdRef,
    setLoading: setContextLoading,
    addMessage,
    setMessages,
    setCurrentConversationId,
    clearMessages,
    createNewConversation,
    loadConversation,
    deleteConversation,
    updateConversationInList,
    addConversationToList
  } = useChat();
  
  // Local state for UI only
  const [input, setInput] = useState('');
  const [attachedImage, setAttachedImage] = useState(null);
  const [exportingRecipe, setExportingRecipe] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStep, setExportStep] = useState('');
  const dailySuggestions = useMemo(() => getDailySuggestions(t), [t, i18n.language]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const voiceControlRef = useRef(null);
  
  // Use context loading or create local alias
  const loading = contextLoading;
  const setLoading = setContextLoading;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Note: loadConversations, saveConversations, createNewConversation, 
  // loadConversation, deleteConversation are provided by useChat() context

  const updateConversation = (id, newMessages) => {
    updateConversationInList(id, newMessages);
  };

  const handleImageAttach = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('common.error'));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedImage(reader.result);
        toast.success(t('common.loading'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExportRecipe = async (messageIndex) => {
    try {
      setExportingRecipe(messageIndex);
      setExportProgress(0);
      
      const message = messages[messageIndex];
      
      // Passo 1: Parseando receita
      setExportStep('Processando receita...');
      setExportProgress(20);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const recipeData = parseRecipeFromMessage(message.content);
      
      // Passo 2: Gerando imagem
      setExportStep('Gerando imagem da receita...');
      setExportProgress(40);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Passo 3: Calculando nutrição
      setExportStep('Calculando informações nutricionais...');
      setExportProgress(60);
      
      // Criar receita no banco
      const response = await axios.post(`${API}/recipes`, recipeData);
      
      // Passo 4: Finalizando
      setExportStep('Finalizando...');
      setExportProgress(80);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setExportProgress(100);
      
      // Toast de sucesso intuitivo
      toast.success(t('chatAssistant.recipeReady'), {
        duration: 2000,
        description: 'Redirecionando...'
      });
      
      // Aguardar um momento e redirecionar
      setTimeout(() => {
        navigate(`/recipe/${response.data.id}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting recipe:', error);
      toast.error(t('recipe.errorSavingRecipe'));
    } finally {
      setExportingRecipe(null);
      setExportProgress(0);
      setExportStep('');
    }
  };

  const handleVoiceTranscript = (transcript, shouldSend = true) => {
    console.log('📝 Transcript recebido:', transcript, 'shouldSend:', shouldSend);
    console.log('🔍 Messages atual:', messages.length, 'messages');
    console.log('🔍 CurrentConversationId:', currentConversationId);
    
    if (shouldSend) {
      // Send immediately with the transcript
      handleSendWithText(transcript);
    } else {
      // Just replace the input (don't accumulate)
      setInput(transcript.trim());
    }
  };

  const handleSendWithText = async (text, playVoice = true) => {
    const messageText = text || input;
    
    if (!messageText.trim() && !attachedImage) {
      console.log('⚠️ Nada para enviar');
      return;
    }

    console.log('🎯 === INÍCIO handleSendWithText ===');
    console.log('📤 Enviando mensagem:', messageText);

    // NOVO: Verificar se é pedido de receita e se usuário tem limite
    const isRecipeRequest = /receita|recipe|como fazer|me ensina|prepare|cozinhar|prato/i.test(messageText);
    
    if (isRecipeRequest) {
      try {
        const limitCheck = await axios.get(`${API}/subscription/usage-status`);
        const { subscription, usage, limits } = limitCheck.data;
        
        const isPremium = subscription.plan === 'premium' || subscription.plan === 'family' || subscription.status === 'trial';
        
        if (!isPremium && usage.recipes_this_month >= limits.recipes_per_month) {
          // BLOQUEAR: Limite atingido, não gerar receita
          const limitMessage = {
            role: 'assistant',
            content: `😊 Desculpe, mas você já atingiu o limite de **${limits.recipes_per_month} receitas por mês** do plano Gratuito.\n\n💎 Para continuar criando receitas e ter acesso ilimitado, assine o **Premium** ou **Familiar**!\n\n👑 Clique no botão Premium no menu superior para conhecer os planos e benefícios.`
          };
          
          setMessages(prev => [...prev, limitMessage]);
          
          toast.error('Limite de receitas atingido! 🚫', {
            duration: 4000,
            description: 'Assine Premium para receitas ilimitadas.'
          });
          
          return; // NÃO enviar mensagem para IA
        }
      } catch (error) {
        console.error('Error checking limit:', error);
        // Se falhar, continua (fail-safe)
      }
    }

    console.log('🔍 Estado ANTES - currentConversationId:', currentConversationId);
    console.log('🔍 Estado ANTES - messages.length:', messages.length);
    console.log('🔍 Estado ANTES - messages:', messages);

    // Create conversation ID if doesn't exist
    const convId = currentConversationId || Date.now().toString();
    if (!currentConversationId) {
      setCurrentConversationId(convId);
      currentConversationIdRef.current = convId;
      console.log('🆕 Nova conversa criada:', convId);
    } else {
      console.log('♻️ Usando conversa existente:', convId);
    }

    let userMessage = { role: 'user', content: messageText };
    if (attachedImage) {
      userMessage.image = attachedImage;
    }

    console.log('📝 Mensagens ANTES de adicionar nova:', messages.length);
    const newMessages = [...messages, userMessage];
    console.log('📝 Mensagens DEPOIS de adicionar nova:', newMessages.length);
    console.log('📝 newMessages array:', newMessages);
    
    setMessages(newMessages);
    console.log('✅ setMessages chamado com', newMessages.length, 'mensagens');
    
    // Context handles persistence automatically
    
    setInput(''); // Clear input after sending
    const imageToSend = attachedImage;
    setAttachedImage(null);

    try {
      setLoading(true);

      console.log('📜 Histórico da conversa a enviar:', newMessages.length - 1, 'mensagens (excluindo a atual)');
      console.log('📤 Enviando para API:', { messageText, historyLength: newMessages.length - 1, conversationId: convId });

      // Send history WITHOUT the current message (API will add it)
      const historyToSend = newMessages.slice(0, -1);
      
      // CRÍTICO: Limpar histórico para evitar serialização cíclica
      const cleanHistory = historyToSend.map(msg => ({
        role: msg.role,
        content: msg.content,
        image: msg.image || undefined
      }));

      const response = await axios.post(`${API}/ai/chat`, {
        message: messageText,
        conversation_history: cleanHistory,  // Usar histórico limpo
        image: imageToSend || undefined,
        conversation_id: convId
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      const finalMessages = [...newMessages, aiMessage];
      setMessages(finalMessages);

      // Context handles persistence automatically

      console.log('✅ Resposta recebida');
      console.log('📊 Total de mensagens agora:', finalMessages.length);

      // Don't auto-play audio - user will click "Ouvir Resposta" button if they want
      // This allows them to read the recipe first before starting interactive reading
      
      // Save or create conversation using context functions
      const existingConv = conversations.find(c => c.id === convId);
      
      if (!existingConv) {
        // Create new conversation using context
        addConversationToList({
          id: convId,
          title: messageText.substring(0, 30) + '...',
          messages: finalMessages,
          createdAt: new Date().toISOString()
        });
        console.log('💾 Nova conversa salva:', convId);
      } else {
        // Update existing conversation using context
        updateConversation(convId, finalMessages);
        console.log('💾 Conversa atualizada:', convId);
      }
    } catch (error) {
      console.error('❌ Error chatting:', error);
      toast.error(t('chatAssistant.errorSendingMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    handleSendWithText(input, false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/30 via-white to-orange-50/30">
      <NavigationMenu />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-2 mb-3">
            <MessageCircle className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-4xl font-bold text-amber-900 mb-2">{t('chatAssistant.title')}</h1>
          <p className="text-gray-600">{t('chatAssistant.description')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Histórico */}
          <Card className="lg:col-span-1 p-4 border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm h-[400px] md:h-[500px] lg:h-[600px]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 text-sm md:text-base">{t('chatAssistant.conversations')}</h2>
              <Button
                data-testid="new-conversation-button"
                onClick={createNewConversation}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <ScrollArea className="h-[320px] md:h-[420px] lg:h-[520px]">
              <div className="space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    {t('chatAssistant.noConversationsYet')}
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`group p-3 rounded-lg cursor-pointer transition-all ${
                        currentConversationId === conv.id
                          ? 'bg-orange-100 border border-orange-300'
                          : 'hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div 
                          className="flex-1 min-w-0"
                          onClick={() => loadConversation(conv.id)}
                        >
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {conv.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {conv.messages.length} mensagens
                          </p>
                        </div>
                        <Button
                          onClick={() => deleteConversation(conv.id)}
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-3 p-6 border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
            <ScrollArea className="h-[460px] mb-4 pr-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Bot className="w-16 h-16 mb-4" />
                  <p className="text-center">Olá! Sou seu assistente culinário.</p>
                  <p className="text-center text-sm mt-2">Como posso ajudar?</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex gap-3 ${
                        msg.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                      )}
                      
                      <div className="flex flex-col gap-2 max-w-[80%]">
                        <div
                          className={`p-4 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                        
                        {msg.image && (
                          <img 
                            src={msg.image} 
                            alt="Attached" 
                            className="rounded-lg max-h-48 object-cover"
                          />
                        )}
                        
                        {/* Botão Exportar Receita */}
                        {msg.role === 'assistant' && detectRecipeInMessage(msg.content) && (
                          <div className="self-start w-full max-w-xs space-y-2">
                            <Button
                              data-testid={`export-recipe-${index}`}
                              onClick={() => handleExportRecipe(index)}
                              disabled={exportingRecipe === index}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white shadow-sm w-full"
                            >
                              {exportingRecipe === index ? (
                                <div className="flex items-center justify-center gap-2 w-full">
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  <span className="text-sm">{exportProgress}%</span>
                                </div>
                              ) : (
                                <>
                                  <BookPlus className="w-4 h-4 mr-2" />
                                  Salvar no Livro de Receitas
                                </>
                              )}
                            </Button>
                            {exportingRecipe === index && (
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div 
                                    className="bg-green-600 h-2 transition-all duration-300 ease-out"
                                    style={{ width: `${exportProgress}%` }}
                                  />
                                </div>
                                <p className="text-xs text-gray-600 mt-1">{exportStep}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Botão Ouvir Resposta */}
                        {msg.role === 'assistant' && (
                          <Button
                            onClick={() => voiceControlRef.current?.playAudio(msg.content)}
                            size="sm"
                            variant="outline"
                            className="self-start border-orange-300 hover:bg-orange-50"
                          >
                            <Volume2 className="w-4 h-4 mr-2" />
                            {t('chatAssistant.listenResponse')}
                          </Button>
                        )}
                      </div>

                      {msg.role === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3 justify-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div className="bg-gray-100 p-4 rounded-2xl">
                        <div className="flex gap-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Image Preview */}
            {attachedImage && (
              <div className="mb-3 relative inline-block">
                <img 
                  src={attachedImage} 
                  alt="To attach" 
                  className="h-20 rounded-lg border-2 border-orange-300"
                />
                <button
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Input - SEMPRE em 2 linhas no mobile */}
            <div className="chat-input-container">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageAttach}
                className="hidden"
              />
              
              {/* Linha 1: Controles (Upload + Voice) - APENAS MOBILE */}
              <div className="flex items-center gap-2 mb-3 md:hidden">
                <Button
                  data-testid="attach-button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  size="icon"
                  className="border-gray-300 hover:bg-orange-50"
                  disabled={loading}
                >
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </Button>

                {/* Voice Control */}
                <VoiceControl
                  ref={voiceControlRef}
                  onTranscript={handleVoiceTranscript}
                  autoSend={true}
                  disabled={loading}
                />
              </div>
              
              {/* Linha 2: Input + Send (SEMPRE separado no mobile) */}
              <div className="flex items-center gap-2">
                {/* Controles desktop (inline) */}
                <div className="hidden md:flex items-center gap-2">
                  <Button
                    data-testid="attach-button-desktop"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    size="icon"
                    className="border-gray-300 hover:bg-orange-50"
                    disabled={loading}
                  >
                    <Paperclip className="w-5 h-5 text-gray-600" />
                  </Button>

                  <VoiceControl
                    ref={voiceControlRef}
                    onTranscript={handleVoiceTranscript}
                    autoSend={true}
                    disabled={loading}
                  />
                </div>

                <Input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder={t('chatAssistant.typeMessage')}
                  className="flex-1 border-gray-300"
                  disabled={loading}
                />
                
                <Button
                  data-testid="send-button"
                  onClick={handleSend}
                  disabled={loading || (!input.trim() && !attachedImage)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Help Section - Static Instructions Below Input */}
            <div className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-900 mb-3 text-sm">🎤 {t('chatAssistant.howToUseVoice')}:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-white p-3 rounded border border-blue-100">
                  <p className="font-semibold text-blue-700 mb-1">💬 {t('chatAssistant.conversationMode')}:</p>
                  <p className="text-gray-600">{t('chatAssistant.conversationModeInstructions')}</p>
                </div>
                
                <div className="bg-white p-3 rounded border border-orange-100">
                  <p className="font-semibold text-orange-700 mb-1">🎙️ {t('chatAssistant.recordingMode')}:</p>
                  <p className="text-gray-600">{t('chatAssistant.recordingModeInstructions')}</p>
                </div>
                
                <div className="bg-white p-3 rounded border border-green-100">
                  <p className="font-semibold text-green-700 mb-1">🔊 {t('chatAssistant.listenResponses')}:</p>
                  <p className="text-gray-600">{t('chatAssistant.listenResponsesInstructions')}</p>
                </div>
                
                <div className="bg-white p-3 rounded border border-purple-100">
                  <p className="font-semibold text-purple-700 mb-1">📖 {t('chatAssistant.interactiveReading')}:</p>
                  <p className="text-gray-600">{t('chatAssistant.interactiveReadingInstructions')}</p>
                </div>
              </div>
            </div>

            {/* Suggestions */}
            {messages.length === 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">{t('chatAssistant.suggestions')}:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 assistant-suggestions">
                  {dailySuggestions.map((question, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="text-left justify-start h-auto py-2 px-3 text-xs sm:text-sm border-gray-300 hover:bg-orange-50 hover:border-orange-300 whitespace-normal"
                      onClick={() => setInput(question)}
                    >
                      {question}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatAssistantPage;